import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { width, type WidthProps } from 'styled-system';

import Carousel from 'common/components/Carousel';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import type { BoxSizingProps, FlexShrinkProps } from 'common/theme/system-utilities';
import { boxSizing, flexShrink } from 'common/theme/system-utilities';
import { articleToTrackingProps } from 'common/tracking/articleToTrackingProps';
import type { RecommendationItemProps } from 'common/types/recommendation';
import RecommendationSlide from './RecommendationSlide';
import type { RecommendationsSliderBaseProps, RecommendationsSliderConfig } from './types';

export interface RecommendationsCarouselProps
  extends
    Pick<
      RecommendationsSliderBaseProps,
      | 'type'
      | 'withWishlistButton'
      | 'eventDataList'
      | 'title'
      | 'titleId'
      | 'isLazy'
      | 'withDiscountLabelBackButton'
    >,
    Omit<RecommendationsSliderConfig, 'slideWidth' | 'isObserved'> {
  width: number;
  slides: RecommendationItemProps[];
  onAfterSlide: (activeSlide: number) => void;
}

const SlideContainer = styled.article<WidthProps & BoxSizingProps & FlexShrinkProps>`
  ${width};
  ${boxSizing};
  ${flexShrink};
`;

const slideContainerStyles: ComponentProps<typeof SlideContainer> = {
  boxSizing: 'border-box',
  flexShrink: 0,
};

const RecommendationsCarousel = ({
  widthConfig,
  gapConfig,
  slides,
  onAfterSlide,
  type,
  withWishlistButton,
  eventDataList,
  title,
  titleId,
  isLazy,
  withDiscountLabelBackButton,
}: RecommendationsCarouselProps) => {
  const isSmallVariant = useFeatureFlagIsOn(GrowthbookFlag.RecommendationsSliderSmaller);

  return (
    <Carousel
      afterSlide={onAfterSlide}
      slideGap={gapConfig}
      title={title}
      titleId={titleId}
      withTopControls
      withBoxShadow
    >
      {slides.map(({ article, ...params }, index) => (
        <SlideContainer key={index} width={widthConfig} {...slideContainerStyles}>
          <RecommendationSlide
            {...article}
            tracking={articleToTrackingProps(article)}
            {...params}
            withWishlistButton={withWishlistButton}
            key={`recommendation-slide-${index}`}
            path={article.images[0].path}
            widthConfig={widthConfig}
            index={index}
            type={type}
            eventDataList={eventDataList}
            isLazy={isLazy}
            withDiscountLabelBackButton={withDiscountLabelBackButton}
            isSmallVariant={isSmallVariant}
          />
        </SlideContainer>
      ))}
    </Carousel>
  );
};

export default RecommendationsCarousel;
